import { Link } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";

/**
 * /conditions — Conditions Générales d'Utilisation (CGU) + Confidentialité (FR).
 */
export default function Conditions() {
  return (
    <div className="min-h-screen sl-hero-bg pt-28 pb-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/compte" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[var(--sl-primary)] mb-6" data-testid="cgu-back">
          <ArrowLeft size={14} /> Retour au compte
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <span className="w-12 h-12 rounded-2xl bg-[var(--sl-primary)]/10 grid place-items-center text-[var(--sl-primary)]"><Shield size={22} /></span>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold">Conditions d&apos;utilisation</h1>
            <p className="text-gray-600 text-sm mt-1">CGU + Politique de confidentialité Swiftlink-Africa.</p>
          </div>
        </div>

        <div className="sl-card space-y-6 text-sm leading-relaxed text-gray-700" data-testid="cgu-content">
          <Section title="1. Objet">
            <p>
              Les présentes Conditions Générales d&apos;Utilisation (« CGU ») régissent l&apos;utilisation
              du service de transfert d&apos;argent Swiftlink-Africa accessible sur le site
              <a href="https://swiftlink-africa.com" className="text-[var(--sl-primary)] hover:underline">{" "}swiftlink-africa.com</a>{" "}
              (ci-après « le Service »). Le Service permet aux utilisateurs (« Expéditeurs ») d&apos;envoyer
              de l&apos;argent à des bénéficiaires sur les corridors suivants :
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>🇹🇬 Togo → 🇬🇦 Gabon (Mobile Money)</li>
              <li>🇬🇦 Gabon → 🇫🇷 France (PayPal / Wero / Virement instantané SEPA)</li>
              <li>🇬🇦 Gabon → 🇷🇼 Rwanda (Mobile Money MTN / Tigo / Airtel)</li>
            </ul>
          </Section>

          <Section title="2. Acceptation des CGU">
            <p>
              L&apos;utilisation du Service implique l&apos;acceptation pleine et entière des présentes
              CGU. Tout Expéditeur qui ne souhaite pas accepter ces conditions doit renoncer à
              utiliser le Service.
            </p>
          </Section>

          <Section title="3. Description du Service">
            <p>
              Swiftlink-Africa intervient en tant qu&apos;<strong>agent de transfert manuel</strong> :
              chaque transaction fait l&apos;objet d&apos;une vérification humaine par un agent agréé. Le
              processus type est le suivant :
            </p>
            <ol className="list-decimal pl-5 mt-2 space-y-1">
              <li>L&apos;Expéditeur saisit les informations du bénéficiaire et le montant.</li>
              <li>L&apos;Expéditeur effectue le dépôt Mobile Money au numéro affiché et téléverse la capture d&apos;écran.</li>
              <li>Un agent Swiftlink-Africa vérifie le dépôt puis exécute le paiement au bénéficiaire.</li>
              <li>L&apos;agent confirme la réception et téléverse la preuve de paiement.</li>
              <li>L&apos;Expéditeur peut suivre l&apos;avancement en temps réel grâce à son code unique <code className="font-mono text-xs bg-gray-100 px-1 rounded">SL-XXXXXXXX</code>.</li>
            </ol>
          </Section>

          <Section title="4. Obligations de l&apos;Expéditeur">
            <ul className="list-disc pl-5 space-y-1">
              <li>Fournir des informations exactes et à jour (nom, téléphone, bénéficiaire, etc.).</li>
              <li>Téléverser une capture d&apos;écran <strong>authentique</strong> et <strong>lisible</strong> du dépôt effectué.</li>
              <li>Pour toute transaction ≥ <strong>300&nbsp;000 FCFA</strong> (XOF ou XAF), fournir une pièce d&apos;identité valide (CNI, passeport ou permis) conformément aux obligations LCB-FT.</li>
              <li>Ne pas utiliser le Service à des fins illégales (blanchiment, financement du terrorisme, fraude).</li>
            </ul>
          </Section>

          <Section title="5. Frais et délais">
            <p>Les frais sont indiqués en temps réel sur l&apos;application et varient selon le corridor :</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li><strong>Togo → Gabon :</strong> frais ajustés régulièrement (voir calculateur en ligne).</li>
              <li><strong>Gabon → France :</strong> 15 % de frais. Remise de 15 % avec un code de parrainage valide (transfert ≥ 200 000 FCFA).</li>
              <li><strong>Gabon → Rwanda :</strong> 12,5 % de frais. Remise de 10 % avec un code de parrainage valide (transfert ≥ 100 000 FCFA).</li>
              <li><strong>France → Gabon :</strong> taux fixe 1 € = 656 FCFA. Frais 0 % via PayPal Friends &amp; Family (mode Entre proches), minimum 20 €.</li>
            </ul>
            <p className="mt-2">
              Les délais habituels sont de <strong>1 heure</strong> en heures ouvrées, sous réserve
              de la disponibilité des opérateurs Mobile Money et des banques partenaires.
            </p>
          </Section>

          <Section title="6. Annulation et remboursement">
            <p>
              Vous pouvez annuler une transaction <strong>tant que celle-ci n&apos;a pas encore été
              vérifiée par l&apos;agent</strong> (statut « En attente »). Une fois la transaction
              vérifiée et envoyée, l&apos;annulation n&apos;est plus possible.
            </p>
            <p className="mt-2">
              En cas de problème (erreur de bénéficiaire, retard anormal, etc.), contactez immédiatement
              notre support via la page <Link to="/faq" className="text-[var(--sl-primary)] hover:underline">FAQ &amp; Support</Link>.
            </p>
          </Section>

          <Section title="7. Données personnelles & RGPD">
            <p>
              Swiftlink-Africa collecte et traite vos données personnelles dans le cadre strict de
              l&apos;exécution du Service et du respect des obligations légales (LCB-FT).
            </p>
            <p className="mt-2"><strong>Données collectées :</strong></p>
            <ul className="list-disc pl-5 mt-1 space-y-1">
              <li>Identité (nom, prénom).</li>
              <li>Coordonnées (téléphone, email).</li>
              <li>Pièce d&apos;identité (uniquement pour les transactions ≥ 300&nbsp;000 FCFA).</li>
              <li>Justificatifs de paiement (capture d&apos;écran du dépôt Mobile Money).</li>
              <li>Historique des transactions.</li>
            </ul>
            <p className="mt-2"><strong>Finalités :</strong></p>
            <ul className="list-disc pl-5 mt-1 space-y-1">
              <li>Exécution du transfert.</li>
              <li>Lutte contre le blanchiment de capitaux et le financement du terrorisme.</li>
              <li>Service client et résolution des litiges.</li>
            </ul>
            <p className="mt-2"><strong>Conservation :</strong> les données sont conservées pendant 5 ans après la dernière transaction conformément aux obligations légales.</p>
            <p className="mt-2"><strong>Vos droits :</strong> vous disposez à tout moment d&apos;un droit d&apos;accès, de rectification, d&apos;effacement, et de portabilité de vos données. Pour les exercer, écrivez à <a href="mailto:contact@swiftlink-africa.com" className="text-[var(--sl-primary)] hover:underline">contact@swiftlink-africa.com</a>.</p>
          </Section>

          <Section title="8. Sécurité">
            <p>
              Toutes les communications avec le Site sont chiffrées (HTTPS/TLS). Les justificatifs
              de paiement et pièces d&apos;identité sont stockés sur un service d&apos;objets sécurisé et
              ne sont accessibles qu&apos;aux agents autorisés via un code à 6 chiffres unique. Les mots
              de passe utilisateur sont hachés (bcrypt) — Swiftlink-Africa ne peut jamais consulter
              votre mot de passe en clair.
            </p>
          </Section>

          <Section title="9. Programme de parrainage">
            <p>
              Chaque utilisateur dispose d&apos;un code de parrainage unique au format
              <code className="font-mono text-xs bg-gray-100 px-1 rounded">SLXXXXXX</code>.
              Lorsqu&apos;un nouveau filleul utilise ce code lors d&apos;un transfert
              <strong> Gabon → France</strong>, il bénéficie d&apos;une remise de
              <strong> 15 % sur les frais</strong>. Le code ne peut pas être utilisé par le parrain
              lui-même. Cette offre est valable sans limite de durée et sans limite du nombre de filleuls.
            </p>
          </Section>

          <Section title="10. Limitation de responsabilité">
            <p>
              Swiftlink-Africa ne peut être tenu responsable des dommages indirects (perte de chance,
              perte commerciale, etc.) résultant de l&apos;utilisation du Service. Notre responsabilité
              est strictement limitée au montant de la transaction concernée, hors frais.
            </p>
          </Section>

          <Section title="11. Modifications des CGU">
            <p>
              Swiftlink-Africa se réserve le droit de modifier les présentes CGU à tout moment. La
              version applicable est celle en vigueur à la date de votre transaction. En cas de
              modification substantielle, vous serez informé via l&apos;application ou par email.
            </p>
          </Section>

          <Section title="12. Droit applicable & litiges">
            <p>
              Les présentes CGU sont régies par le droit en vigueur au Togo. En cas de litige, vous
              êtes invité à privilégier une résolution amiable en contactant notre support. À défaut
              d&apos;accord, le litige sera soumis à la compétence exclusive des tribunaux compétents.
            </p>
          </Section>

          <div className="pt-2 text-xs text-gray-400">
            Dernière mise à jour : février 2026.
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/mentions-legales" className="sl-btn-outline text-sm" data-testid="cgu-go-legal">Mentions légales</Link>
          <Link to="/faq" className="sl-btn-outline text-sm" data-testid="cgu-go-faq">Support · FAQ</Link>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading font-bold text-base mb-2 text-gray-900">{title}</h2>
      <div className="text-sm leading-relaxed">{children}</div>
    </section>
  );
}
